import { FC, useState } from 'react';
import {
  Group,
  Title,
  Text,
  Badge,
  Stack,
  Card,
  Button,
  Grid,
  Divider,
} from '@mantine/core';
import { IconEdit, IconMail, IconPhone, IconMapPin } from '@tabler/icons-react';
import { formatHumanName } from '@medplum/core';
import { Document, Loading } from '@medplum/react';
import { useTranslation } from 'react-i18next';
import { ARKON } from '@consts/systems.consts';
import { usePractitioner } from '@hooks/usePractitioner';
import CreatePractitionerModal from './ainigma/CreatePractitionerModal';

// -----------------------------------------------------------------------------
export const PractitionerPage: FC = () => {
  const { t } = useTranslation('practitioners');
  const practitioner = usePractitioner();
  const [editModalOpen, setEditModalOpen] = useState(false);

  // ---------------------------------------------------------------------------
  if (!practitioner) {
    return (
      <Document>
        <Loading />
      </Document>
    );
  }

  // ---------------------------------------------------------------------------
  const name = formatHumanName(practitioner.name?.[0]);
  const organizationTag = practitioner.meta?.tag?.find(
    (tag) => tag.system === ARKON.CS.PRACTITIONER.ORGANIZATION.BASE
  );

  const isExternal =
    !!organizationTag && organizationTag.code !== ARKON.CS.PRACTITIONER.ORGANIZATION.CODES.INTERNAL;

  // Get contact information
  const email = practitioner.telecom?.find((t) => t.system === 'email')?.value;
  const mobilePhone = practitioner.telecom?.find(
    (t) => t.system === 'phone' && t.use === 'mobile'
  )?.value;
  const workPhone = practitioner.telecom?.find(
    (t) => t.system === 'phone' && t.use === 'work'
  )?.value;

  // Get address
  const address = practitioner.address?.[0];

  // Get qualifications
  const qualification = practitioner.qualification?.[0];

  // ---------------------------------------------------------------------------
  return (
    <Document>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <div>
            <Group gap="md" align="center" mb="xs">
              <Title order={1}>{name}</Title>
              <Badge
                size="lg"
                color={isExternal ? 'orange' : 'blue'}
                variant="light"
              >
                {isExternal
                  ? (organizationTag?.display ?? t('page.external'))
                  : t('page.internal')}
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              {t('page.practitionerId')}: {practitioner.id}
            </Text>
          </div>

          <Button
            leftSection={<IconEdit size={16} />}
            onClick={() => setEditModalOpen(true)}
          >
            {t('buttons.edit')}
          </Button>
        </Group>
        <Divider />
        {/* Main Content */}
        <Grid>
          {/* Contact Information */}
          <Grid.Col span={6}>
            <Card withBorder p="lg">
              <Title order={3} mb="md">
                {t('details.contactInformation')}
              </Title>
              <Stack gap="sm">
                {email && (
                  <Group gap="sm">
                    <IconMail size={16} />
                    <Text>{email}</Text>
                  </Group>
                )}
                {mobilePhone && (
                  <Group gap="sm">
                    <IconPhone size={16} />
                    <div>
                      <Text size="sm" c="dimmed">
                        {t('details.mobilePhone')}
                      </Text>
                      <Text>{mobilePhone}</Text>
                    </div>
                  </Group>
                )}
                {workPhone && (
                  <Group gap="sm">
                    <IconPhone size={16} />
                    <div>
                      <Text size="sm" c="dimmed">
                        {t('details.workPhone')}
                      </Text>
                      <Text>{workPhone}</Text>
                    </div>
                  </Group>
                )}
              </Stack>
            </Card>
          </Grid.Col>

          {/* Professional Information */}
          <Grid.Col span={6}>
            <Card withBorder p="lg">
              <Title order={3} mb="md">
                {t('details.professionalInformation')}
              </Title>
              <Stack gap="sm">
                {qualification && (
                  <>
                    {qualification.code?.text && (
                      <div>
                        <Text size="sm" c="dimmed">
                          {t('details.qualification')}
                        </Text>
                        <Text>{qualification.code.text}</Text>
                      </div>
                    )}
                    {qualification.code?.coding?.[0]?.display && (
                      <div>
                        <Text size="sm" c="dimmed">
                          {t('details.specialization')}
                        </Text>
                        <Text>{qualification.code.coding[0].display}</Text>
                      </div>
                    )}
                  </>
                )}
                {isExternal && organizationTag?.display && (
                  <div>
                    <Text size="sm" c="dimmed">
                      {t('details.organization')}
                    </Text>
                    <Text>{organizationTag.display}</Text>
                  </div>
                )}
              </Stack>
            </Card>
          </Grid.Col>

          {/* Address Information */}
          {address && (
            <Grid.Col span={12}>
              <Card withBorder p="lg">
                <Title order={3} mb="md">
                  <Group gap="sm">
                    <IconMapPin size={20} />
                    {t('details.address')}
                  </Group>
                </Title>
                <Stack gap="xs">
                  {address.line?.map((line, index) => (
                    <Text key={index}>{line}</Text>
                  ))}
                  <Group gap="md">
                    {address.city && <Text>{address.city}</Text>}
                    {address.state && <Text>{address.state}</Text>}
                    {address.postalCode && <Text>{address.postalCode}</Text>}
                  </Group>
                  {address.country && <Text>{address.country}</Text>}
                </Stack>
              </Card>
            </Grid.Col>
          )}
        </Grid>{' '}
        {/* Edit Modal */}
        <CreatePractitionerModal
          practitioner={practitioner}
          opened={editModalOpen}
          onClose={() => setEditModalOpen(false)}
        />
      </Stack>
    </Document>
  );
};
